/* import __COLOCATED_TEMPLATE__ from './setup-and-go-live-phone.hbs'; */
/* RESPONSIBLE TEAM: team-phone */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ChannelType } from 'embercom/lib/workflows/fin-workflow-preview';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import type finTrialService from 'embercom/services/fin-trial-service';
import { action } from '@ember/object';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type IntlService from 'embercom/services/intl';
import type FinOptInService from 'embercom/services/fin-opt-in-service';
import { taskFor } from 'ember-concurrency-ts';
import type FinPlaygroundApiService from 'embercom/services/fin-playground-api-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';

interface Arguments {
  hasContentReadyForFin: boolean;
  reloadSetupRuleset: (setupType: ChannelType) => Promise<void>;
  phoneSetupRuleset: $TSFixMe;
  openNewLineModal?: () => void;
}

interface Signature {
  Element: HTMLDivElement;
  Args: Arguments;
}

export default class SetupAndGoLivePhone extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare contentEditorService: $TSFixMe;
  @service declare router: RouterService;
  @service declare finTrialService: finTrialService;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare finOptInService: FinOptInService;
  @service permissionsService: $TSFixMe;
  @service declare finPlaygroundApiService: FinPlaygroundApiService;
  @service declare intercomEventService: $TSFixMe;

  @tracked ruleset: $TSFixMe;
  @tracked openSectionId: string | undefined;
  // Track if the interaction was triggered by the popover change, so we can prevent toggling the section.
  @tracked isPopoverChange = false;

  // API Token state
  @tracked apiToken: string | null = null;
  @tracked isLoadingToken = false;
  @tracked isCreatingToken = false;

  @tracked selectedPhoneNumberId: string | undefined;
  @tracked selectedNumberConfiguration: FinVoiceNumberConfiguration | undefined;

  get canViewPhoneNumberPurchase() {
    return this.appService.app.canUseFeature('team-fin-voice-phone-number-purchase');
  }

  get numberConfigurations() {
    return this.finVoiceConfigService.numberConfigurations;
  }

  get hasNumberConfigurations() {
    return Object.keys(this.numberConfigurations).length > 0;
  }

  get phoneNumbers() {
    return this.finVoiceConfigService.phoneNumbers;
  }

  get parentAppUrl(): string {
    return this.router.urlFor(
      'apps.app.automation.fin-ai-agent.setup',
      this.appService.app.parent_app_id,
    );
  }

  constructor(owner: unknown, args: any) {
    super(owner, args);

    if (this.appService.app.finBlockedInTestApp) {
      return;
    }

    let currentRuleset = this.contentEditorService.ruleset;
    this.contentEditorService.unregister(currentRuleset);

    this.updateRuleset(this.args.phoneSetupRuleset, true);

    if (Object.keys(this.numberConfigurations).length) {
      this.openSectionId = Object.keys(this.numberConfigurations)[0];
    }
  }

  willDestroy(): void {
    super.willDestroy();
  }

  @action
  onOpenSectionChange(openSectionId: string) {
    if (!this.hasNumberConfigurations) {
      return;
    }

    if (this.isPopoverChange) {
      this.isPopoverChange = false;
      return;
    }
    this.openSectionId = openSectionId;
  }

  @action
  onPopoverInteraction() {
    this.isPopoverChange = true;
  }

  @action
  goToVoiceTesting() {
    if (this.appService.app.isStandaloneApp) {
      this.router.transitionTo('apps.app.phone.standalone.deploy.test');
    } else {
      this.router.transitionTo({ queryParams: { tab: 'voice-playground' } });
    }
  }

  @action
  async updatePercentageRollout(
    phoneNumberId: string,
    rollOutPercentageIn: number | undefined,
    rollOutPercentageNin: number | undefined,
  ) {
    try {
      let ruleset = this.ruleset;

      if (!ruleset) {
        return;
      }

      // to get here, customer has seen the Fin AI Agent overview landing page with all the terms for using Fin
      if (ruleset.isDraft && !this.finOptInService.hasOptedInToFin) {
        await taskFor(this.finOptInService.postAcceptOptIn).perform();
      }

      await this.finVoiceConfigService.updatePercentageRollout(
        phoneNumberId,
        rollOutPercentageIn,
        rollOutPercentageNin,
      );

      await this.updateRuleset(ruleset);

      if (ruleset.isLive) {
        await this.finTrialService.openFinRetrialConfirmationModal();
      }

      this.notificationsService.notifyConfirmation(
        this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.success'),
      );
    } catch (error) {
      console.error('error', error);
      this.notificationsService.notifyResponseError(error, {
        default: this.intl.t('operator.fin.setup.setup-and-go-live.notification-messages.failure'),
      });
    }
  }

  @action
  async updateRuleset(ruleset: $TSFixMe, register = false) {
    this.ruleset = await this.store.findRecord('matching-system/ruleset', ruleset.id);
    await this.args.reloadSetupRuleset('phone');

    if (register && this.contentEditorService.ruleset?.id !== this.ruleset.id) {
      this.contentEditorService.register({ ruleset: this.ruleset });
    }
  }

  @action
  setSelectedLine(phoneNumberId: string, numberConfiguration: FinVoiceNumberConfiguration) {
    this.selectedPhoneNumberId = phoneNumberId;
    this.selectedNumberConfiguration = numberConfiguration;
    this.openSectionId = phoneNumberId;
  }

  @action
  talkToUs() {
    this.finVoiceConfigService.talkToUs();
  }

  @action
  resetSelectedLine() {
    this.selectedPhoneNumberId = undefined;
    this.selectedNumberConfiguration = undefined;
    this.openSectionId = undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::SetupAndGoLivePhone': typeof SetupAndGoLivePhone;
  }
}
